import { Layer } from "@carbon/react";
import { FC } from "react";
import { Controller, FormProvider, useFormState } from "react-hook-form";

import { InlineNotification } from "src/components/Notifications";
import { useT } from "src/internationalization";
import { LocaleKeys } from "src/internationalization/CustomIntlProvider";
import { AlertValidationDecision, AlertValidationDecisionValues } from "src/types/alert";
import { cn } from "src/utils/string.utils.ts";

import { BlockedDecisionTile } from "./BlockedDecisionTile";
import { CaseAlertsHistory } from "./CaseAlertsHistory";
import { DecisionFormControl } from "./useDecisionForm";

const DECISION_LABELS: Record<
  AlertValidationDecision,
  { title: LocaleKeys; description: LocaleKeys }
> = {
  compliant: {
    title: "alerts.confirmFraud.decision.compliant.title",
    description: "alerts.confirmFraud.decision.compliant.description",
  },
  fraudPrevented: {
    title: "alerts.confirmFraud.decision.fraudPrevented.title",
    description: "alerts.confirmFraud.decision.fraudPrevented.description",
  },
  provenFraudWithFinancialLoss: {
    title: "alerts.confirmFraud.decision.proven_fraud_with_financial_loss.title",
    description: "alerts.confirmFraud.decision.proven_fraud_with_financial_loss.description",
  },
  provenFraudWithNoFinancialLoss: {
    title: "alerts.confirmFraud.decision.provedFraudWithNoFinancialLoss.title",
    description: "alerts.confirmFraud.decision.provedFraudWithNoFinancialLoss.description",
  },
};

export type DecisionFormProps = {
  decisionFormControl: DecisionFormControl;
  onDecisionChange: (decision: AlertValidationDecision) => void;
  caseId: string;
  caseNumber: string;
  blockedByDecision: Map<AlertValidationDecision, boolean>;
  blockedReasonByDecision: Map<AlertValidationDecision, string | undefined>;
  hasBlockedDecisions: boolean;
};

export const DecisionForm: FC<DecisionFormProps> = ({
  decisionFormControl,
  caseId,
  caseNumber,
  onDecisionChange,
  blockedByDecision,
  blockedReasonByDecision,
  hasBlockedDecisions,
}) => {
  const t = useT();
  const { errors } = useFormState({ control: decisionFormControl.control });

  return (
    <Layer className="flex flex-col">
      <FormProvider {...decisionFormControl.methods}>
        <Controller
          name="decision"
          control={decisionFormControl.control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className={"mb-5"}>
              {hasBlockedDecisions && (
                <div className="mb-4">
                  <InlineNotification
                    kind="warning"
                    title={t("alerts.confirmFraud.decision.blocked.title")}
                    subtitle={t("alerts.confirmFraud.decision.blocked.subtitle")}
                    fullWidth
                    hideCloseButton
                  />
                </div>
              )}
              <div className="mb-5 flex flex-row items-stretch gap-4">
                {AlertValidationDecisionValues.map((value) => {
                  const labels = DECISION_LABELS[value];
                  const blocked = blockedByDecision.get(value) ?? false;
                  const blockedReason = blockedReasonByDecision.get(value);

                  return (
                    <BlockedDecisionTile
                      key={value}
                      value={value}
                      title={t(labels.title)}
                      description={t(labels.description)}
                      selected={field.value === value}
                      onChange={() => {
                        field.onChange(field.value === value ? null : value);
                        onDecisionChange(value);
                      }}
                      blocked={blocked}
                      blockedReason={blockedReason}
                      data-decision-input
                      className={cn(
                        errors.decision &&
                          "!outline-supportError border-none outline -outline-offset-2 outline-2"
                      )}
                      tabIndex={0}
                      aria-pressed={field.value === value}
                    />
                  );
                })}
              </div>
              {errors.decision && (
                <span className="text-textError flex gap-2 items-center">
                  {t("general.form.field.required")}
                </span>
              )}
            </div>
          )}
        />
      </FormProvider>
      <div className="flex-1 min-h-[240px] min-w-0 flex flex-col">
        <CaseAlertsHistory caseId={caseId} caseNumber={caseNumber} />
      </div>
    </Layer>
  );
};
